import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { NodeFieldLabel } from "@/components/doc/right-sidebar/active-node-style/fields/NodeTextField";
import { CursorContextProvider } from "@/components/input/CursorContextProvider";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { twMerge } from "@/utils/twMerge";

import { InlineNodeField } from "./InlineNodeField";

import type { LabelPlacement } from "@/components/doc/right-sidebar/active-node-style/fields/NodeTextField";
import type { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import type { VariableNodeModel } from "@/models/nodes/VariableNodeModel";

type Props = {
  node: VariableNodeModel | FunctionNodeModel;
  className?: string;
  labelPlacement?: LabelPlacement;
  action?: React.ReactNode;
};

export const ParametersField = observer(function ParametersField({
  node,
  className,
  labelPlacement = "outside",
  action,
}: Props) {
  const { doc } = node;
  const t = useTranslations();
  const parametersContextId = `parameters-field-${node.id}`;

  const resetSelectionAndMove = () => {
    const { cursor, selection } = doc.cursorContext();

    if (selection.isSelected(node)) {
      selection.resetSelection();
      cursor.moveAfterNode(node);
    }
  };

  const handleOnClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();

    let inline: InlineNodeModel | undefined;

    resetSelectionAndMove();
    doc.transact(() => {
      inline = doc.create(InlineNodeModel);
      node.appendChild(inline);
    }, "ParametersField:handleOnClick");

    if (inline) {
      queueMicrotask(() => {
        doc.cursorContextId = parametersContextId;
        doc.cursor.moveTo(inline, 0);
      });
    }
  };

  if (node.parameters.length === 0) {
    return (
      <NodeFieldLabel
        as="div"
        label={t("sidebar.activeNode.fields.arguments")}
        className={twMerge(
          "py-0.5 pr-0.5 min-h-[28px] flex-1 min-w-0 shadow-block dark:shadow-block-inverted",
          action && "pr-7",
          className,
        )}
        onClick={handleOnClick}
        labelPlacement={labelPlacement}
      >
        {action && (
          <div
            role="presentation"
            className="absolute right-0 top-0 bottom-0 p-0.5 pl-0 flex items-center"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {action}
          </div>
        )}
      </NodeFieldLabel>
    );
  }

  return (
    <CursorContextProvider id={parametersContextId} doc={node.doc} boundary={node.parameters}>
      <InlineNodeField
        node={node.parameters}
        onLabelMouseDown={resetSelectionAndMove}
        label={t("sidebar.activeNode.fields.arguments")}
        className={twMerge("shadow-block dark:shadow-block-inverted", className)}
        labelPlacement={labelPlacement}
        action={action}
        contentClassName={action ? "pr-7" : undefined}
      />
    </CursorContextProvider>
  );
});
